import { AutoComplete, Form, Modal } from 'antd';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ConditionSuggestionEntityTypeEnum from '~Api/Application/ConditionSuggestionEntityTypeEnum';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IBorrower from '~Api/Application/IBorrower';
import IConditionSuggestion from '~Api/Application/IConditionSuggestion';
import {
    applicationConditionSuggestionsListAction,
    applicationConditionsAddAction,
} from '~Applications/actions';
import { applicationConditionSuggestionsSelector } from '~Applications/selectors';

interface IOption {
    label: string;
    score: number;
    value: string;
}

export default function ConditionModal(props: {
    application?: IApplication;
    applicationProperty?: IApplicationProperty;
    applicationBorrower?: IBorrower;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const {
        application,
        applicationBorrower,
        applicationProperty,
        isOpen,
        onCancel,
    } = props;

    const [ conditionName, setConditionName ] = useState<string>(null);
    const [ options, setOptions ] = useState<IOption[]>([]);

    const suggestions: IConditionSuggestion[] = useSelector(applicationConditionSuggestionsSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!suggestions) {
            dispatch(applicationConditionSuggestionsListAction());
        }
    }, [
        dispatch,
        suggestions,
    ]);

    const onClickAdd: () => void = useCallback(() => {
        const applicationUuid: string = application ? application.uuid : null;
        const applicationBorrowerUuid: string = applicationBorrower ? applicationBorrower.uuid : null;
        const applicationPropertyUuid: string = applicationProperty ? applicationProperty.uuid : null;

        dispatch(applicationConditionsAddAction(
            applicationUuid,
            applicationBorrowerUuid,
            applicationPropertyUuid,
            conditionName,
        ));

        setConditionName(null);
        onCancel();
    }, [
        application,
        applicationBorrower,
        applicationProperty,
        conditionName,
        dispatch,
        onCancel,
    ]);

    const onSearchConditionName: (value: string) => void = useCallback((value: string) => {
        const searchValue: string = value.toLocaleLowerCase();

        let entityType: ConditionSuggestionEntityTypeEnum = ConditionSuggestionEntityTypeEnum.Application;
        if (applicationBorrower) {
            entityType = ConditionSuggestionEntityTypeEnum.Borrower;
        } else if (applicationProperty) {
            entityType = ConditionSuggestionEntityTypeEnum.Property;
        }

        const results: IOption[] = [];

        _.each(suggestions, (suggestion: IConditionSuggestion) => {
            if (suggestion.entityType !== entityType) {
                return;
            }

            if (suggestion.name.toLocaleLowerCase().includes(searchValue)) {
                results.push({
                    label: suggestion.name,
                    score: suggestion.score,
                    value: suggestion.name,
                });
            }
        });

        setConditionName(value);
        setOptions(_.sortBy(results, ['score']));
    }, [
        applicationBorrower,
        applicationProperty,
        suggestions,
    ]);

    const onSelectConditionName: (value: string) => void = useCallback((value: string) => {
        setConditionName(value);
    }, []);

    if (!suggestions) {
        return null;
    }

    const entityName: string = applicationProperty ? applicationProperty.dealProperty.formattedAddress : (applicationBorrower ? applicationBorrower.dealBorrower.formattedName : application.code);

    return (
        <Modal
            destroyOnClose={true}
            okText='Add Condition'
            onCancel={onCancel}
            onOk={onClickAdd}
            open={isOpen}
            title={`Add Condition for ${entityName}`}
            wrapClassName='application-conditions-add-modal'
            width={800}
        >
            <Form.Item
                label='Name'
                className='condition'
                extra={`${conditionName ? conditionName.length : 0} / 255`}
            >
                <AutoComplete
                    dropdownMatchSelectWidth={300}
                    maxLength={255}
                    onSearch={onSearchConditionName}
                    onSelect={onSelectConditionName}
                    options={_.slice(options, 0, 20)}
                    value={conditionName}
                />
            </Form.Item>
        </Modal>
    );
}
